// src/components/Registro.js
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";

const Registro = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [clave, setClave] = useState("");
  const [confirmClave, setConfirmClave] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Estados para mostrar/ocultar contraseña
  const [showClave, setShowClave] = useState(false);
  const [showConfirmClave, setShowConfirmClave] = useState(false);

  const inputsRef = useRef([]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Indicadores de contraseña segura
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Función para verificar fortaleza de contraseña en tiempo real
  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const isPasswordStrong = Object.values(passwordStrength).every(Boolean);

  // =========================
  // 1️⃣ SOLICITAR CÓDIGO
  // =========================
  const enviarCodigo = async () => {
    if (!email.trim()) {
      setError("Por favor ingrese su correo electrónico");
      return;
    }
    if (!validarEmail(email)) {
      setError("Ingrese un correo electrónico válido");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
  await axios.post("/auth/solicitar-codigo", { email });
  setStep(2);
} catch (err) {
  setError(
    err.response?.data?.message ||
    err.response?.data?.error ||
    "No se pudo conectar con el servidor"
  );
} finally {
  setIsLoading(false);
}
  };

  // =========================
  // 2️⃣ VERIFICAR CÓDIGO
  // =========================
  const verificarCodigo = async () => {
    if (codigo.length !== 6) {
      setError("Ingrese un código de 6 dígitos válido");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
  await axios.post("/auth/verificar-codigo", { email, codigo });
  setStep(3);
} catch (err) {
  setError(
    err.response?.data?.message ||
    err.response?.data?.error ||
    "Error al verificar el código"
  );
} finally {
  setIsLoading(false);
}
  };

  // =========================
  // 3️⃣ CREAR CONTRASEÑA
  // =========================
  const crearCuenta = async () => {
    if (!isPasswordStrong) {
      setError("Por favor cumple todos los requisitos de la contraseña");
      return;
    }

    if (clave !== confirmClave) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setError("");
    setIsLoading(true);
       try {
  await axios.post("/auth/crear-password", { email, password: clave, codigo });
  setShowSuccess(true);
  let time = 10;
  setCountdown(time);

  const interval = setInterval(() => {
    time--;
    setCountdown(time);
    if (time === 0) {
      clearInterval(interval);
      navigate("/login");
    }
  }, 1000);
} catch (err) {
  setError(
    err.response?.data?.message ||
    err.response?.data?.error ||
    "No se pudo conectar con el servidor"
  );
} finally {
  setIsLoading(false);
}
  };

  return (
    <div className="vh-100 d-flex justify-content-center align-items-center bg-light">
      <div className="card shadow-lg p-4" style={{ maxWidth: "420px", width: "100%" }}>
        <h3 className="text-center text-success mb-4">
          Registro Socios ADUTEM
        </h3>

        {/* PASO 1 */}
        {step === 1 && (
          <>
            <div className="mb-3">
              <label className="form-label">Correo electrónico</label>
              <input
                type="email"
                className="form-control"
                placeholder="Ingrese su correo"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button
              className="btn btn-success w-100"
              onClick={enviarCodigo}
              disabled={isLoading}
            >
              {isLoading ? "Enviando..." : "Siguiente"}
            </button>
          </>
        )}

        {/* PASO 2 */}
        {step === 2 && (
          <>
            <h5 className="text-center fw-bold text-success mb-2">Verificación de Código</h5>
            <p className="text-center text-dark mb-4">
              Se envió un código de 6 dígitos a tu correo <br />
              <strong>{email}</strong>
            </p>
            <p className="text-center text-danger fw-semibold mb-3">
              El código expirará en 10 minutos
            </p>

            <div className="d-flex justify-content-between mb-4">
              {[...Array(6)].map((_, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="form-control text-center fs-4"
                  style={{ width: "45px", height: "50px" }}
                  value={codigo[index] || ""}
                  onPaste={(e) => {
                    e.preventDefault();
                    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
                    if (paste.length === 6) {
                      setCodigo(paste);
                      paste.split("").forEach((char, i) => {
                        if (inputsRef.current[i]) inputsRef.current[i].value = char;
                      });
                      inputsRef.current[5]?.focus();
                    }
                  }}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/\D/, "");
                    const nuevoCodigo = codigo.split("");
                    nuevoCodigo[index] = valor;
                    setCodigo(nuevoCodigo.join(""));
                    if (valor && index < 5) inputsRef.current[index + 1].focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !codigo[index] && index > 0) {
                      inputsRef.current[index - 1].focus();
                    }
                  }}
                />
              ))}
            </div>

            <button className="btn btn-success w-100" onClick={verificarCodigo} disabled={isLoading}>
              {isLoading ? "Validando..." : "Validar Código"}
            </button>
          </>
        )}

        {/* PASO 3 - CON INDICADORES EN TIEMPO REAL */}
        {step === 3 && (
          <>
            <div className="mb-3">
              <label className="form-label">Contraseña</label>
              <div className="input-group">
                <input
                  type={showClave ? "text" : "password"}
                  className="form-control"
                  placeholder="Ingresa tu contraseña"
                  value={clave}
                  onChange={(e) => {
                    setClave(e.target.value);
                    checkPasswordStrength(e.target.value);
                  }}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowClave(!showClave)}
                >
                  <i className={`bi ${showClave ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Confirmar contraseña</label>
              <div className="input-group">
                <input
                  type={showConfirmClave ? "text" : "password"}
                  className="form-control"
                  placeholder="Repite tu contraseña"
                  value={confirmClave}
                  onChange={(e) => setConfirmClave(e.target.value)}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowConfirmClave(!showConfirmClave)}
                >
                  <i className={`bi ${showConfirmClave ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                </button>
              </div>
            </div>

            {/* Indicadores de Seguridad */}
            <div className="mb-4">
              <p className="form-label mb-2 fw-semibold text-success">
                Requisitos de contraseña:
              </p>
              <ul className="list-unstyled small">
                <li className={`d-flex align-items-center gap-2 mb-1 ${passwordStrength.length ? 'text-success' : 'text-muted'}`}>
                  {passwordStrength.length ? '✓' : '○'} Mínimo 8 caracteres
                </li>
                <li className={`d-flex align-items-center gap-2 mb-1 ${passwordStrength.uppercase ? 'text-success' : 'text-muted'}`}>
                  {passwordStrength.uppercase ? '✓' : '○'} Al menos una mayúscula (A-Z)
                </li>
                <li className={`d-flex align-items-center gap-2 mb-1 ${passwordStrength.lowercase ? 'text-success' : 'text-muted'}`}>
                  {passwordStrength.lowercase ? '✓' : '○'} Al menos una minúscula (a-z)
                </li>
                <li className={`d-flex align-items-center gap-2 mb-1 ${passwordStrength.number ? 'text-success' : 'text-muted'}`}>
                  {passwordStrength.number ? '✓' : '○'} Al menos un número (0-9)
                </li>
                <li className={`d-flex align-items-center gap-2 ${passwordStrength.special ? 'text-success' : 'text-muted'}`}>
                  {passwordStrength.special ? '✓' : '○'} Al menos un carácter especial (!@#$% etc.)
                </li>
              </ul>
            </div>

            <button
              className="btn btn-success w-100"
              onClick={crearCuenta}
              disabled={isLoading || !isPasswordStrong || clave !== confirmClave || clave.length === 0}
            >
              {isLoading ? "Creando cuenta..." : "Crear cuenta"}
            </button>
          </>
        )}

        {error && <div className="alert alert-danger mt-3">{error}</div>}
      </div>

      {/* MODALES */}
      {isLoading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999 }}>
          <div className="bg-white p-4 rounded shadow text-center">
            <div className="spinner-border text-success mb-3"></div>
            <p className="fw-semibold mb-0">Procesando solicitud…</p>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}>
          <div className="bg-white p-4 rounded shadow text-center">
            <h5 className="text-success fw-bold mb-3">Registro exitoso</h5>
            <p>Tu cuenta ha sido creada correctamente.</p>
            <p className="text-muted">
              Serás redirigido en <strong>{countdown}</strong> segundos.
            </p>
            <button className="btn btn-success w-100" onClick={() => navigate("/login")}>
              Ir ahora a Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registro;